/**
 * CanvasHeader — top of the artifact pane: source-of-pin label and the tab
 * strip (Table / Plot / Report).
 *
 * Tabs are driven by chatStore.canvasTab; clicking a tab switches the
 * store, which the parent ArtifactCanvas observes to choose which artifact
 * variant to render. (The real export lives in the Report tab's "Export PDF";
 * the old header Download / Send-to-Benchmarking stubs were removed.)
 */

import {
  canvasTab,
  canvasTabUserOverridden,
  pinnedArtifact,
  salesModeActive,
  selectionContext,
  unpinArtifact,
} from '../../../state/chatStore.js'
import { t } from '../../../i18n/index.js'

function tabs() {
  return [
    { key: 'table', label: t('ion.canvas.tab.table') },
    { key: 'plot', label: t('ion.canvas.tab.plot') },
    { key: 'report', label: t('ion.canvas.tab.report') },
  ]
}

/**
 * Per-tab availability during a sales-selection flow: the Plot is the entry
 * view (always on), the Table needs a ranking, the Report needs a shortlist.
 * Outside the flow all tabs stay enabled. Returns {table, plot, report}.
 */
function tabAvailability() {
  if (!salesModeActive.value) return { table: true, plot: true, report: true }
  const stage = selectionContext.value.stage
  const hasRanked = pinnedArtifact.value?.type === 'ranked_cells_table'
  return {
    plot: true,
    table: hasRanked,
    report: stage === 'shortlist' || stage === 'trade_offs',
  }
}

const TAB_UNAVAILABLE_HINT = {
  table: 'Available after the first ranking',
  report: 'Available once you reach the shortlist',
}

/**
 * @param {object} props
 * @param {object} [props.artifact] — pinned artifact reference (for the source label)
 */
export function CanvasHeader({ artifact }) {
  const current = canvasTab.value
  const tabList = tabs()
  const avail = tabAvailability()

  return (
    <header class="ion-canvas-header">
      <div class="ion-canvas-header__source">
        {artifact ? (
          <>
            <span class="ion-canvas-header__pin" aria-hidden="true" />
            <span class="ion-canvas-header__source-label">
              {t('ion.canvas.pinned')}
            </span>
          </>
        ) : (
          <span class="ion-canvas-header__source-label ion-canvas-header__source-label--muted">
            {t('ion.canvas.unpinned')}
          </span>
        )}
      </div>

      {/* B2: the tab strip is a FLOW control — Plot / Table / Report are the
          guided flow's three stages of the same shortlist. Outside the flow it
          was three tabs over one artifact, two of which had nothing to show;
          there the panel is something the user opened on purpose, so it gets a
          close button instead. */}
      {!salesModeActive.value ? (
        <button
          type="button"
          class="ion-canvas-header__close"
          onClick={() => unpinArtifact()}
          aria-label="Close panel"
          title="Close panel"
        >
          ×
        </button>
      ) : (
      <nav class="ion-canvas-header__tabs" aria-label="Artifact view">
        {tabList.map((tab) => {
          const disabled = !avail[tab.key]
          return (
            <button
              key={tab.key}
              type="button"
              class={`ion-canvas-header__tab ${current === tab.key ? 'ion-canvas-header__tab--active' : ''} ${disabled ? 'ion-canvas-header__tab--disabled' : ''}`}
              aria-pressed={current === tab.key}
              aria-disabled={disabled}
              disabled={disabled}
              title={disabled ? TAB_UNAVAILABLE_HINT[tab.key] : undefined}
              onClick={() => {
                if (disabled) return
                canvasTab.value = tab.key
                canvasTabUserOverridden.value = true
              }}
            >
              {tab.label}
            </button>
          )
        })}
      </nav>
      )}
    </header>
  )
}
